'use client';
import React from 'react';
import classNames from 'classnames';

import { Dialog, ErrorsBox, RadioGroup, RadioOption } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import {
  EditorialMarketAlertsNotificationParams,
  NotificationManager,
  SendGroup,
  SendGroupData,
} from '@benzinga/notification-manager';
import styles from './EditorialPushNotificationModal.module.scss';

interface EditorialPushNotificationModalProps {
  isOpen: boolean;
  nid: number;
  title: string;
  url: string;
  onClose: () => void;
}

interface SendGroupModalData extends SendGroupData {
  count: number | undefined;
}

export const EditorialPushNotificationModal: React.FC<EditorialPushNotificationModalProps> = ({
  isOpen,
  nid,
  onClose,
  title,
  url,
}) => {
  const session = React.useContext(SessionContext);
  const [notificationTitle, setNotificaitonTitle] = React.useState<string>(title);
  const [sendGroup, setSendGroup] = React.useState<SendGroup>('everyone');
  const notificationManager = React.useMemo(() => session.getManager(NotificationManager), [session]);

  const intialSendGroupData = React.useMemo((): SendGroupModalData[] => {
    const newSendGroupData = notificationManager.getStaticSendGroupData();
    const result: SendGroupModalData[] = [
      {
        count: undefined,
        description: '',
        id: 5, // Not actually 5
        name: 'Everyone',
        status: 1,
      },
      ...(newSendGroupData as SendGroupModalData[]),
    ];
    return result;
  }, [notificationManager]);

  const [sendGroupData, setSendGroupData] = React.useState<SendGroupModalData[]>(intialSendGroupData);

  const sendGroupOptions: RadioOption[] = React.useMemo(() => {
    return sendGroupData
      .filter(sendGroup => sendGroup.name !== 'Off')
      .map(sendGroup => {
        return {
          id: sendGroup.id,
          label:
            typeof sendGroup.count === 'number' ? `${sendGroup.name} <b>(${sendGroup.count} Sent)</b>` : sendGroup.name,
          name: sendGroup.name,
          value: sendGroup.id,
        };
      });
  }, [sendGroupData]);

  const [selectedSendGroupData, setSelectedSendGroupData] = React.useState<RadioOption | undefined>(
    sendGroupOptions?.[0],
  );

  const [success, setSuccess] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      const newSendGroupData = await notificationManager.getSendGroupData(true);
      const marketAlertsAnalytics = await notificationManager.getMarketAlertsAnalytics();

      const result = newSendGroupData.ok?.data;
      if (result) {
        const sendTodayCount = marketAlertsAnalytics.ok?.data?.sent_today_count;
        let newResult: SendGroupModalData[] = result.map(sendGroupItem => {
          return {
            ...sendGroupItem,
            count: sendTodayCount?.data.find(item => item.id === sendGroupItem.id)?.count,
          };
        });
        newResult = [
          {
            count: sendTodayCount?.total,
            description: '',
            id: 5, // Not actually 5
            name: 'Everyone',
            status: 1,
          },
          ...newResult,
        ];
        setSendGroupData(newResult);
      }
    })();
  }, [notificationManager]);

  const handleSendEditorialPushNotificaiton = React.useCallback(async () => {
    setErrorMessage(null);
    setSuccess(false);

    if (notificationTitle.trim() === '') {
      setErrorMessage('Title must not be empty');
      return;
    }

    const selectedSendGroupIds = sendGroupData
      .filter(sendGroup => selectedSendGroupData?.value === sendGroup.id)
      .map(sendGroup => sendGroup.id);

    const params: EditorialMarketAlertsNotificationParams = {
      nid,
      send_group: sendGroup,
      send_group_data: selectedSendGroupIds,
      title: notificationTitle,
      url,
    };

    if (sendGroup === 'everyone') {
      params.send_group_data = [];
    }

    const result = await notificationManager.sendEditorialMarketAlertsNotification(params);
    console.log('EDITORIAL_PUSH_NOTIFICATION_RESULT', result, params);

    if (result?.ok?.ok) {
      setSuccess(result.ok.ok);
      setSendGroupData(previousSendGroupData => {
        const specificSendGroup = previousSendGroupData.map(previousSendGroup => {
          if (sendGroup === 'everyone' && previousSendGroup.name === 'Everyone') {
            previousSendGroup.count = Number(previousSendGroup.count) + 3;
          }
          if (selectedSendGroupIds.includes(previousSendGroup.id) || sendGroup === 'everyone') {
            previousSendGroup.count = Number(previousSendGroup.count) + 1;
          }
          return previousSendGroup;
        });
        return specificSendGroup;
      });
    } else if (result.err) {
      setErrorMessage(result.err?.message);
    }
  }, [nid, notificationManager, notificationTitle, selectedSendGroupData?.value, sendGroup, sendGroupData, url]);

  const handleChangeNotificaitonTitle = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNotificaitonTitle(value);
  }, []);

  const handleChangeSelectedSendGroupData = React.useCallback((option: RadioOption) => {
    setSelectedSendGroupData(option);
    if (option.name === 'Everyone') {
      setSendGroup('everyone');
    } else {
      setSendGroup('marketAlerts');
    }
  }, []);

  return (
    <div className={classNames(styles.container, 'modal-container rounded')}>
      <Dialog
        actionText="Send"
        border="rounded-sm"
        closeOnOutsideClick={true}
        closeText="Cancel"
        onAction={handleSendEditorialPushNotificaiton}
        onRequestClose={onClose}
        open={isOpen}
        subtitle="Send editorial push notifications to users on the Benzinga Mobile app"
        title="Push Notification"
      >
        <div className="modal-content flex flex-col w-full">
          <label className="font-semibold" htmlFor="notification-title-textarea">
            Title
          </label>
          <textarea
            className="notification-title-textarea"
            id="notification-title-textarea"
            onChange={handleChangeNotificaitonTitle}
            value={notificationTitle}
          />
          <label className="font-semibold" htmlFor="notification-send-group-data-input">
            Send Group
          </label>
          <div className="min-h-[88px]">
            <RadioGroup
              className="whitespace-normal"
              onChange={handleChangeSelectedSendGroupData}
              options={sendGroupOptions}
              selected={selectedSendGroupData}
            />
          </div>
          <ErrorsBox error={errorMessage} type="box" />
          {success && (
            <span className="w-full text-green-500 bg-green-100 border border-green-500 rounded px-2 py-1 mt-2 text-sm">
              Notification successfully sent!
            </span>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default EditorialPushNotificationModal;
